import React, { useState } from "react";
import { Youtube, Instagram, Twitter, Music2, Users, BadgeCheck, Pencil, X, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Compact creator/influencer section for a listing detail page.
// Shows the seller's social handles + self-reported follower counts. When the
// signed-in user is the listing owner (or admin) an "Edit" pencil opens an
// inline editor that POSTs to /api/listings/:id/influencer.

const fmtFollowers = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return String(v || 0);
};

// Normalise @handle or full URL into a clickable link. Falls back to plain text.
const linkFor = (platform, raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const handle = s.replace(/^@/, "");
  switch (platform) {
    case "youtube": return `https://youtube.com/@${handle}`;
    case "instagram": return `https://instagram.com/${handle}`;
    case "tiktok": return `https://tiktok.com/@${handle}`;
    case "twitter": return `https://x.com/${handle}`;
    default: return null;
  }
};

const PLATFORMS = [
  { key: "youtube",   Icon: Youtube,   label: "YouTube",   fCountKey: "followers_yt", color: "text-red-600 hover:bg-red-50" },
  { key: "instagram", Icon: Instagram, label: "Instagram", fCountKey: "followers_ig", color: "text-fuchsia-600 hover:bg-fuchsia-50" },
  { key: "tiktok",    Icon: Music2,    label: "TikTok",    fCountKey: "followers_tt", color: "text-stone-900 hover:bg-stone-100" },
  { key: "twitter",   Icon: Twitter,   label: "X",         fCountKey: "followers_x",  color: "text-sky-600 hover:bg-sky-50" },
];

export default function InfluencerCard({ listing, isOwner, onSaved }) {
  const inf = listing?.extra?.influencer;
  const [editOpen, setEditOpen] = useState(false);

  // Owner with no profile yet → show a compact "Add" CTA instead of hiding entirely.
  if (!inf && !isOwner) return null;

  return (
    <div className="mt-6 bg-white border border-stone-200 rounded-2xl p-6" data-testid="influencer-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />
          <span className="text-[10px] uppercase tracking-widest font-bold text-fuchsia-700">Creator & influencer</span>
          {inf?.verified && (
            <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded-full" data-testid="influencer-verified">
              <BadgeCheck size={11} /> Verified
            </span>
          )}
        </div>
        {isOwner && (
          <button
            onClick={() => setEditOpen(true)}
            className="text-xs text-stone-500 hover:text-stone-900 inline-flex items-center gap-1"
            data-testid="influencer-edit-btn"
          >
            <Pencil size={12} /> {inf ? "Edit" : "Add creator profile"}
          </button>
        )}
      </div>

      {!inf && isOwner && (
        <div className="text-sm text-stone-500">
          Add your YouTube / Instagram / TikTok / X handles + follower counts so buyers know they're
          dealing with a legit creator. Great for fashion, food, tech, coaching listings.
        </div>
      )}

      {inf && (
        <>
          {inf.niche && (
            <div className="text-sm text-stone-600 mb-3">
              Niche: <span className="font-semibold text-stone-900">{inf.niche}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2" data-testid="influencer-handles">
            {PLATFORMS.map(({ key, Icon, label, fCountKey, color }) => {
              const url = linkFor(key, inf[key]);
              if (!url) return null;
              const followers = inf[fCountKey];
              return (
                <a
                  key={key}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border border-stone-200 rounded-full text-xs font-semibold ${color}`}
                  data-testid={`influencer-${key}`}
                >
                  <Icon size={14} /> {label}
                  {followers > 0 && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-black bg-stone-100 text-stone-700 px-1.5 py-0.5 rounded-full">
                      <Users size={9} /> {fmtFollowers(followers)}
                    </span>
                  )}
                </a>
              );
            })}
          </div>
        </>
      )}

      {editOpen && (
        <InfluencerEditor
          listing={listing}
          initial={inf || {}}
          onClose={() => setEditOpen(false)}
          onSaved={(next) => { setEditOpen(false); onSaved?.(next); }}
        />
      )}
    </div>
  );
}

function InfluencerEditor({ listing, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    youtube: initial.youtube || "",
    instagram: initial.instagram || "",
    tiktok: initial.tiktok || "",
    twitter: initial.twitter || "",
    followers_yt: initial.followers_yt || "",
    followers_ig: initial.followers_ig || "",
    followers_tt: initial.followers_tt || "",
    followers_x: initial.followers_x || "",
    niche: initial.niche || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        youtube: form.youtube.trim() || null,
        instagram: form.instagram.trim() || null,
        tiktok: form.tiktok.trim() || null,
        twitter: form.twitter.trim() || null,
        followers_yt: Number(form.followers_yt) || null,
        followers_ig: Number(form.followers_ig) || null,
        followers_tt: Number(form.followers_tt) || null,
        followers_x: Number(form.followers_x) || null,
        niche: form.niche.trim() || null,
      };
      const r = await api.post(`/listings/${listing.listing_id}/influencer`, payload);
      toast.success("Creator profile saved");
      onSaved?.(r.data.influencer);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose} data-testid="influencer-editor">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div className="font-bold text-lg text-stone-900">Creator profile</div>
          <button onClick={onClose} className="p-1 text-stone-500 hover:text-stone-800" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <p className="text-xs text-stone-500 mb-4">Self-reported. Paste handles (e.g. <span className="font-mono">@yourname</span>) or full URLs.</p>

        <div className="space-y-3">
          <Field label="Niche (e.g. fashion, food, tech)"
            value={form.niche} onChange={(v) => set("niche", v)} testId="inf-niche" />
          <div className="grid grid-cols-3 gap-2">
            <Field label="YouTube" placeholder="@handle" value={form.youtube} onChange={(v) => set("youtube", v)} testId="inf-yt" wrapClass="col-span-2" />
            <Field label="Subscribers" placeholder="0" value={form.followers_yt} onChange={(v) => set("followers_yt", v.replace(/[^\d]/g, ""))} testId="inf-yt-count" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Instagram" placeholder="@handle" value={form.instagram} onChange={(v) => set("instagram", v)} testId="inf-ig" wrapClass="col-span-2" />
            <Field label="Followers" placeholder="0" value={form.followers_ig} onChange={(v) => set("followers_ig", v.replace(/[^\d]/g, ""))} testId="inf-ig-count" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="TikTok" placeholder="@handle" value={form.tiktok} onChange={(v) => set("tiktok", v)} testId="inf-tt" wrapClass="col-span-2" />
            <Field label="Followers" placeholder="0" value={form.followers_tt} onChange={(v) => set("followers_tt", v.replace(/[^\d]/g, ""))} testId="inf-tt-count" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="X / Twitter" placeholder="@handle" value={form.twitter} onChange={(v) => set("twitter", v)} testId="inf-x" wrapClass="col-span-2" />
            <Field label="Followers" placeholder="0" value={form.followers_x} onChange={(v) => set("followers_x", v.replace(/[^\d]/g, ""))} testId="inf-x-count" />
          </div>
        </div>

        <Button onClick={save} disabled={saving} className="w-full mt-5 rounded-full bg-[#E05E36] hover:bg-[#c94e2a]" data-testid="influencer-save">
          {saving ? <><Loader2 size={14} className="inline animate-spin mr-1" /> Saving…</> : "Save creator profile"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testId, wrapClass = "" }) {
  return (
    <div className={wrapClass}>
      <label className="text-[10px] uppercase tracking-widest font-bold text-stone-500">{label}</label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testId} className="mt-1" />
    </div>
  );
}
